// Browser-safe wire shapes shared by the referral discovery contracts.
import { z } from 'zod'

export const rosterDoctorSchema = z.object({
  npi: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  credential: z.string().optional(),
  taxonomyLabel: z.string(),
  phone: z.string().optional(),
  fax: z.string().optional(),
})

// 'not-checked': below the enrichment cut-off, so no job is running for it — the UI must
// not show a spinner that cannot resolve.
export const rosterViewSchema = z.object({
  status: z.enum(['pending', 'ready', 'failed', 'not-checked']),
  doctors: z.array(rosterDoctorSchema),
})

export const referralLocationSchema = z.object({
  placeId: z.string(),
  name: z.string(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lat: z.number(),
  lon: z.number(),
  distanceMiles: z.number(),
  phone: z.string().optional(),
  website: z.string().optional(),
  mapsUrl: z.string(),
  rating: z.number().optional(),
  ratingCount: z.number().optional(),
  referralCount: z.number().int(),
  roster: rosterViewSchema,
})

export const referralDoctorSchema = rosterDoctorSchema.extend({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lat: z.number().optional(),
  lon: z.number().optional(),
  distanceMiles: z.number().optional(),
  referralCount: z.number().int(),
})

export const frequentRecipientSchema = z.object({
  key: z.string(),
  npi: z.string().optional(),
  placeId: z.string().optional(),
  name: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  credential: z.string().optional(),
  practiceName: z.string().optional(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().optional(),
  fax: z.string(),
  distanceMiles: z.number().optional(),
  referralCount: z.number().int(),
  lastReferredAt: z.string(),
  bookmarked: z.boolean(),
})

export const FAX_SOURCES = [
  'saved',
  'directory:cms',
  'directory:humana',
  'nppes',
  'website',
] as const

export const faxCandidateSchema = z.object({ fax: z.string(), source: z.enum(FAX_SOURCES) })

export const resolvedRecipientSchema = z.object({
  npi: z.string().optional(),
  placeId: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  credential: z.string().optional(),
  practiceName: z.string().optional(),
  specialtyLabel: z.string(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().optional(),
  faxCandidates: z.array(faxCandidateSchema),
  recommendedFax: z.string().optional(),
})

export const latSchema = z.coerce.number().min(-90).max(90)
export const lonSchema = z.coerce.number().min(-180).max(180)

// What the order flow sends to save a recipient, and what a fax send records.
export const RecipientInputSchema = z.object({
  npi: z
    .string()
    .regex(/^\d{10}$/)
    .optional(),
  // Google place ids are URL-safe. The id becomes part of a FHIR identifier search, where
  // ',' means OR, so nothing else may reach it.
  placeId: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,300}$/)
    .optional(),
  name: z.string().trim().min(1).max(200),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  credential: z.string().max(40).optional(),
  practiceName: z.string().max(200).optional(),
  specialtyCode: z.string().max(20),
  specialtyLabel: z.string().max(100),
  street: z.string().max(200),
  city: z.string().max(100),
  state: z.string().max(2),
  zip: z.string().max(10),
  phone: z.string().max(30).optional(),
  fax: z.string().min(1).max(30),
})
export type RecipientInput = z.infer<typeof RecipientInputSchema>
